'use client'

import { useCart, useModals } from '@/lib/hooks'
import { cn, formatPrice, handleNumberInput } from '@/lib/utils'
import { ChevronDown, Minus, Plus } from 'lucide-react'
import React from 'react'
import { BagCheck, TrashBin2 } from 'solar-icons'
import { toast } from 'sonner'

import {
  Badge,
  Barcode,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
  Thumbnail,
} from '@/components/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface EditCartItemProps extends React.ComponentPropsWithoutRef<'form'> {
  product: Product
}

export const EditCartItem = ({ product }: EditCartItemProps) => {
  const { updateCartProduct, removeProduct } = useCart() as any
  const { closeModal } = useModals()
  const cartId = product?.cart_id
  const productId = product?.id
  const hasWarranty =
    product?.has_warranty === 1 || product?.has_warranty === true

  const [qty, setQty] = React.useState<number>(product?.quantity ?? 1)
  const [discountType, setDiscountType] = React.useState<string>('fixed')
  const [discount, setDiscount] = React.useState<number>(
    product?.discount_per_qty === 0 ? undefined : product?.discount_per_qty,
  )
  const [selectedAddons, setSelectedAddons] = React.useState<any>({})

  React.useEffect(() => {
    if (product?.categorized_add_ons) {
      const initialAddons: Record<string, any> = {}
      for (const category of product.categorized_add_ons) {
        const selectedInCategory = category.add_ons
          .filter((addon: any) => addon.is_selected)
          .map((addon: any) => ({
            addon_price_id: addon.default_price.price_id,
            addon_id: addon.id,
            quantity: addon.selected_qty || 1,
          }))

        if (selectedInCategory.length > 0) {
          initialAddons[category.category_id] = selectedInCategory
        }
      }
      setSelectedAddons(initialAddons)
    }
  }, [product])

  const validateAddons = () => {
    /**
     * Check for `is_required` addons in product?.add_on_categories array
     * Check if selectedAddons has any `is_required` addons
     * If not, show a toast error
     * If all `is_required` addons are selected, continue
     */

    // Get all required addon categories
    const requiredCategories = product?.add_on_categories?.filter(
      (category: any) => category?.is_required,
    )

    // If no required categories, validation passes
    if (!requiredCategories || requiredCategories.length === 0) {
      return true
    }

    // Check if all required categories have at least one addon selected
    const missingCategories = requiredCategories.filter((category: any) => {
      const categoryAddons = selectedAddons[category?.id]
      return !categoryAddons || categoryAddons.length === 0
    })

    // If there are missing required categories, show error
    if (missingCategories.length > 0) {
      const categoryNames = missingCategories
        .map((cat: any) => cat?.name)
        .join(', ')

      toast.error(`Please select at least one addon from: ${categoryNames}`)
      return false
    }

    return true
  }

  const handleSubmit = async (data: FormData) => {
    const qty = Number(data.get('qty'))

    if (!cartId || !productId) {
      return toast.error('Cart ID or Product ID is missing.')
    }

    if ((Number.isNaN(qty) || qty < 1) && !hasWarranty) {
      return toast.error('Quantity must be a number greater than 0.')
    }

    // Validate required addons
    if (!validateAddons()) {
      return
    }

    // Transform selectedAddons object to array of {id, qty} objects
    const addonsWithQuantity = Object.values(selectedAddons)
      .flat()
      .map((addon: any) => ({
        id: addon?.addon_price_id,
        qty: addon?.quantity || 1,
      }))

    try {
      await updateCartProduct({
        cartId,
        productId,
        quantity: !hasWarranty ? qty : product?.quantity,
        discount,
        discountType,
        qtyChange: !hasWarranty
          ? Number(product?.quantity) !== Number(qty)
          : false,
        addon_price_ids: addonsWithQuantity,
      })

      toast.success('Cart item updated successfully.')
      closeModal('EDIT_CART_ITEM')
    } catch (error) {
      toast.error('Failed to update cart item.')
    }
  }

  const handleRemoveProduct = () => {
    removeProduct({ productId, cartId })
    closeModal('EDIT_CART_ITEM')
  }

  const fallbackTitle = `${product?.product_name} ${product?.varient_options}`

  return (
    <form
      className="flex w-full flex-col flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <Thumbnail
          size="lg"
          src={product?.image_url}
          className="border border-gray-100"
        />

        <div className="flex w-full flex-col flex-wrap gap-4">
          <h2 className="max-w-[396px] text-balance text-xl font-medium">
            {product?.variant_label ?? fallbackTitle}
          </h2>

          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4 md:flex-row">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-[36px] px-0" size="md">
                  SKU
                </Badge>
                <span className="font-mono text-sm font-medium text-gray-950">
                  {product?.sku}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="min-w-5 px-1" size="md">
                  <Barcode className="h-3" />
                </Badge>
                <span className="font-mono text-sm font-medium text-gray-950">
                  {product?.barcode}
                </span>
              </div>
            </div>
          </div>

          <div className="flex h-auto w-full flex-col gap-5">
            {!hasWarranty && (
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Quantity
                  </Label>
                  <Separator className="mt-1" />
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    className="max-w-[200px] appearance-none text-center"
                    value={qty}
                    inputSize="lg"
                    min={1}
                    name="qty"
                    onChangeValue={(value) => {
                      setQty(Number(value))
                    }}
                    onKeyDown={handleNumberInput}
                    type="number"
                    autoFocus
                  />
                  <div className="flex w-full items-center gap-2">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        if (qty > 1) {
                          setQty((_prevQty) => _prevQty - 1)
                        }
                      }}
                    >
                      <Minus className="size-5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => setQty((_prevQty) => _prevQty + 1)}
                    >
                      <Plus className="size-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full items-center gap-2">
                <Label className="shrink-0 !text-sm font-medium text-gray-700">
                  {hasWarranty ? 'Discount' : 'Discount per Item'}
                </Label>
                <Separator className="mt-1" />
              </div>

              <div className="flex items-center gap-2">
                <Input
                  className="max-w-[200px] appearance-none text-center"
                  value={discount?.toString()}
                  inputSize="lg"
                  name="discount"
                  onChangeValue={(value) => setDiscount(Number(value))}
                  type="number"
                  placeholder="0"
                />
                <div className="flex w-full items-center gap-2">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full justify-between"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <span>
                          {discountType === 'fixed'
                            ? '(Rs) Fixed Amount'
                            : '(%) Percentage'}
                        </span>
                        <ChevronDown className="size-5" strokeWidth={1.5} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="z-[100] w-[var(--radix-dropdown-menu-trigger-width)] p-0"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <div className="flex w-full flex-col gap-0.5 bg-gray-50 p-1">
                        <DropdownMenuItem
                          className="w-full"
                          asChild
                          onSelect={() => setDiscountType('fixed')}
                        >
                          <Button
                            variant="outline"
                            className="w-full rounded-sm shadow-none"
                          >
                            (Rs) Fixed Amount
                          </Button>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="w-full"
                          asChild
                          onSelect={() => setDiscountType('percentage')}
                        >
                          <Button
                            variant="outline"
                            className="w-full rounded-sm shadow-none"
                          >
                            (%) Percentage
                          </Button>
                        </DropdownMenuItem>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {product?.categorized_add_ons?.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center gap-2">
            <Label className="shrink-0 !text-sm font-medium text-gray-700">
              Addons
            </Label>
            <Separator className="mt-1" />
          </div>

          <Tabs
            defaultValue={product?.categorized_add_ons?.[0]?.category_id}
            className="grid w-full grid-cols-1 gap-4 lg:grid-cols-[200px_1fr] lg:gap-8"
          >
            <TabsList className="m-0 h-auto max-h-[240px] flex-col items-start justify-start gap-1 overflow-y-auto border border-gray-100 bg-transparent p-3">
              {product?.categorized_add_ons.map((category: any) => {
                return (
                  <TabsTrigger
                    key={category?.category_id}
                    value={category?.category_id}
                    className="h-auto min-h-10 w-full"
                  >
                    <div className="w-full hyphens-auto whitespace-normal break-words text-left">
                      {category?.category_name}
                    </div>
                  </TabsTrigger>
                )
              })}
            </TabsList>

            <div className="flex max-h-[240px] w-full flex-col overflow-y-auto rounded-md border border-gray-100 bg-gray-50/20 p-3">
              {product?.categorized_add_ons.map((category: any) => {
                return (
                  <TabsContent
                    key={category?.category_id}
                    value={category?.category_id}
                    className="m-0 flex w-full flex-col gap-1 p-0"
                  >
                    {category?.add_ons.map((addon: any) => {
                      if (!addon?.default_price) return null

                      const hasQuantity =
                        (selectedAddons[category?.category_id]?.find(
                          (item: any) =>
                            item.addon_price_id ===
                            addon?.default_price?.price_id,
                        )?.quantity ?? 0) > 0

                      return (
                        <div
                          key={addon?.id}
                          className={cn(
                            'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 bg-white shadow-card hover:bg-gray-50 lg:flex-row',
                            {
                              'border-gray-500': hasQuantity,
                            },
                          )}
                        >
                          <Checkbox
                            className={cn(
                              'absolute inset-y-0 right-3 my-auto',
                              {
                                hidden: hasQuantity,
                              },
                            )}
                            value={addon?.default_price?.price_id}
                            id={`addon-${addon?.default_price?.price_id}`}
                            checked={
                              selectedAddons[category?.category_id]?.some(
                                (item: any) =>
                                  item.addon_price_id ===
                                  addon?.default_price?.price_id,
                              ) || false
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                // Check max_limit before adding new addon
                                const categoryInfo =
                                  product?.add_on_categories?.find(
                                    (cat: any) =>
                                      cat?.id === category?.category_id,
                                  )

                                if (categoryInfo?.max_limit) {
                                  // Calculate total quantity of all addons in this category
                                  const currentTotal =
                                    selectedAddons[
                                      category?.category_id
                                    ]?.reduce(
                                      (sum: number, item: any) =>
                                        sum + (item.quantity || 1),
                                      0,
                                    ) || 0

                                  if (currentTotal >= categoryInfo.max_limit) {
                                    toast.error(
                                      `Maximum ${categoryInfo.max_limit} item${categoryInfo.max_limit > 1 ? 's' : ''} allowed for ${category?.category_name}`,
                                    )
                                    return
                                  }
                                }

                                setSelectedAddons((prev: any) => ({
                                  ...prev,
                                  [category?.category_id]: [
                                    ...(prev[category?.category_id] || []),
                                    {
                                      addon_price_id:
                                        addon?.default_price?.price_id,
                                      addon_id: addon?.id,
                                      quantity: 1,
                                    },
                                  ],
                                }))
                              } else {
                                // Prevent unchecking if quantity > 1
                                const currentItem = selectedAddons[
                                  category?.category_id
                                ]?.find(
                                  (item: any) =>
                                    item.addon_price_id ===
                                    addon?.default_price?.price_id,
                                )

                                if (currentItem && currentItem.quantity > 0) {
                                  return
                                }

                                setSelectedAddons((prev: any) => {
                                  const categoryAddons = prev[
                                    category?.category_id
                                  ]?.filter(
                                    (item: any) =>
                                      item.addon_price_id !==
                                      addon?.default_price?.price_id,
                                  )

                                  // If no addons left in category, remove the category key
                                  if (
                                    !categoryAddons ||
                                    categoryAddons.length === 0
                                  ) {
                                    const {
                                      [category?.category_id]: _,
                                      ...rest
                                    } = prev
                                    return rest
                                  }

                                  return {
                                    ...prev,
                                    [category?.category_id]: categoryAddons,
                                  }
                                })
                              }
                            }}
                          />
                          <Label
                            htmlFor={`addon-${addon?.default_price?.price_id}`}
                            className={cn(
                              'flex w-full cursor-auto items-center justify-between p-2 !text-sm font-semibold',
                              {
                                'pr-2': hasQuantity,
                                'pr-12': !hasQuantity,
                              },
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <Thumbnail
                                src={addon?.image_url}
                                className="size-8 rounded-md"
                              />
                              <div className="flex flex-col">
                                <span>{addon?.name}</span>
                                <span className="text-sm font-medium text-gray-500">
                                  {formatPrice(
                                    addon?.default_price?.price,
                                    2,
                                    true,
                                  )}
                                </span>
                              </div>
                            </div>

                            {selectedAddons[category?.category_id]?.some(
                              (item: any) =>
                                item.addon_price_id ===
                                addon?.default_price?.price_id,
                            ) && (
                              <div className="flex items-center">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  type="button"
                                  className="size-9"
                                  onClick={(e) => {
                                    e.preventDefault()

                                    const currentItem = selectedAddons[
                                      category?.category_id
                                    ]?.find(
                                      (item: any) =>
                                        item.addon_price_id ===
                                        addon?.default_price?.price_id,
                                    )

                                    // If quantity is 1, remove the item entirely
                                    if ((currentItem?.quantity || 1) === 1) {
                                      setSelectedAddons((prev: any) => {
                                        const categoryAddons = prev[
                                          category?.category_id
                                        ]?.filter(
                                          (item: any) =>
                                            item.addon_price_id !==
                                            addon?.default_price?.price_id,
                                        )

                                        // If no addons left in category, remove the category key
                                        if (
                                          !categoryAddons ||
                                          categoryAddons.length === 0
                                        ) {
                                          const {
                                            [category?.category_id]: _,
                                            ...rest
                                          } = prev
                                          return rest
                                        }

                                        return {
                                          ...prev,
                                          [category?.category_id]:
                                            categoryAddons,
                                        }
                                      })
                                    } else {
                                      // Otherwise, just decrease the quantity
                                      setSelectedAddons((prev: any) => ({
                                        ...prev,
                                        [category?.category_id]: prev[
                                          category?.category_id
                                        ].map((item: any) =>
                                          item.addon_price_id ===
                                          addon?.default_price?.price_id
                                            ? {
                                                ...item,
                                                quantity:
                                                  (item.quantity || 1) - 1,
                                              }
                                            : item,
                                        ),
                                      }))
                                    }
                                  }}
                                >
                                  <Minus className="size-4" strokeWidth={1.5} />
                                </Button>

                                <span className="w-[28px] text-center">
                                  {selectedAddons[category?.category_id]?.find(
                                    (item: any) =>
                                      item.addon_price_id ===
                                      addon?.default_price?.price_id,
                                  )?.quantity ?? 1}
                                </span>

                                <Button
                                  size="icon"
                                  variant="outline"
                                  type="button"
                                  className="size-9"
                                  onClick={(e) => {
                                    e.preventDefault()

                                    const categoryInfo =
                                      product?.add_on_categories?.find(
                                        (cat: any) =>
                                          cat?.id === category?.category_id,
                                      )

                                    if (categoryInfo?.max_limit) {
                                      // Calculate total quantity of all addons in this category
                                      const currentTotal =
                                        selectedAddons[
                                          category?.category_id
                                        ]?.reduce(
                                          (sum: number, item: any) =>
                                            sum + (item.quantity || 1),
                                          0,
                                        ) || 0

                                      if (
                                        currentTotal >= categoryInfo.max_limit
                                      ) {
                                        toast.error(
                                          `Maximum ${categoryInfo.max_limit} item${categoryInfo.max_limit > 1 ? 's' : ''} allowed for ${category?.category_name}`,
                                        )
                                        return
                                      }
                                    }

                                    setSelectedAddons((prev: any) => ({
                                      ...prev,
                                      [category?.category_id]: prev[
                                        category?.category_id
                                      ].map((item: any) =>
                                        item.addon_price_id ===
                                        addon?.default_price?.price_id
                                          ? {
                                              ...item,
                                              quantity:
                                                (item.quantity || 1) + 1,
                                            }
                                          : item,
                                      ),
                                    }))
                                  }}
                                >
                                  <Plus className="size-4" strokeWidth={1.5} />
                                </Button>
                              </div>
                            )}
                          </Label>
                        </div>
                      )
                    })}
                  </TabsContent>
                )
              })}
            </div>
          </Tabs>
        </div>
      )}

      <div className="flex w-full pt-3">
        <Button
          variant="brand"
          className="w-full gap-3 shadow-card"
          size="lg"
          type="submit"
        >
          <BagCheck className="size-5" strokeWidth={1.5} />
          <span>Update Cart</span>
        </Button>

        <Button
          variant="destructive"
          className="w-[100px] gap-3 shadow-card"
          size="lg"
          type="button"
          onClick={handleRemoveProduct}
        >
          <TrashBin2 className="size-5" strokeWidth={1.5} />
        </Button>
      </div>
    </form>
  )
}
